import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { ArrowRight, Car, Banknote, Unlock, CreditCard } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useI18n } from '@/store/I18nContext'

interface HeroProps {
  onOpenDemo: () => void
}

export function Hero({ onOpenDemo }: HeroProps) {
  const { t } = useI18n()

  return (
    <section
      className="pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden relative"
      id="recursos"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-background to-background pointer-events-none" />

      <div className="container mx-auto px-4 relative">
        <div className="text-center max-w-4xl mx-auto space-y-8 animate-fade-in-up">
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter text-foreground leading-[1.1]">
            {t('hero.title1')}{' '}
            <span className="text-primary">{t('hero.titleHighlight')}</span>{' '}
            {t('hero.title2')}
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed font-medium">
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Button
              onClick={onOpenDemo}
              size="lg"
              className="w-full sm:w-auto h-14 px-8 text-lg font-bold tracking-tight shadow-lg"
            >
              {t('hero.getStarted')}
            </Button>
            <Link to="/dashboard" className="w-full sm:w-auto">
              <Button
                variant="outline"
                size="lg"
                className="w-full h-14 px-8 text-lg font-bold tracking-tight group"
              >
                {t('hero.accessSystem')}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-20 relative max-w-5xl mx-auto animate-fade-in">
          <div className="absolute -inset-4 bg-gradient-to-tr from-primary/20 to-secondary/20 blur-3xl rounded-[3rem] -z-10" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="shadow-xl bg-background/80 backdrop-blur-md border-primary/20 hover:-translate-y-1 transition-transform">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-semibold tracking-tight text-muted-foreground">
                  {t('hero.vehiclesInYard')}
                </CardTitle>
                <Car className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-extrabold tracking-tight">
                  42{' '}
                  <span className="text-base font-semibold text-muted-foreground">
                    / 50
                  </span>
                </div>
                <Progress value={84} className="mt-3 h-2" />
                <p className="text-xs font-medium text-muted-foreground mt-2">
                  84% {t('hero.occupancy')}
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-xl bg-background/80 backdrop-blur-md border-primary/20 hover:-translate-y-1 transition-transform delay-75">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-semibold tracking-tight text-muted-foreground">
                  {t('hero.revenueToday')}
                </CardTitle>
                <Banknote className="h-4 w-4 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-extrabold tracking-tight text-emerald-600">
                  R$ 850,00
                </div>
                <p className="text-xs font-medium text-muted-foreground mt-2">
                  42 {t('hero.transactions')}
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-xl bg-background/80 backdrop-blur-md border-primary/20 hover:-translate-y-1 transition-transform delay-100">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-semibold tracking-tight text-muted-foreground">
                  {t('hero.avgTicket')}
                </CardTitle>
                <CreditCard className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-extrabold tracking-tight">
                  R$ 20,23
                </div>
                <p className="text-xs font-medium text-muted-foreground mt-2">
                  {t('hero.perVehicle')}
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-xl bg-background/80 backdrop-blur-md border-primary/20 hover:-translate-y-1 transition-transform delay-150">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-semibold tracking-tight text-muted-foreground">
                  {t('hero.cashStatus')}
                </CardTitle>
                <Unlock className="h-4 w-4 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-extrabold tracking-tight text-emerald-600">
                  {t('hero.open')}
                </div>
                <p className="text-xs font-medium text-muted-foreground mt-2">
                  {t('hero.startedAt')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  )
}
